import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithPhoneNumber, 
  RecaptchaVerifier,
  onAuthStateChanged,
  signOut as firebaseSignOut
} from 'firebase/auth';
import { auth } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmationResult, setConfirmationResult] = useState(null);

  // Load user from localStorage on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('whistlerUser');
    const savedAuth = localStorage.getItem('whistlerAuth');
    
    if (savedUser && savedAuth) {
      try {
        const userData = JSON.parse(savedUser);
        const authData = JSON.parse(savedAuth);
        
        // Check if auth data is still valid (not expired)
        if (authData.expiresAt && new Date(authData.expiresAt) > new Date()) {
          setCurrentUser(userData);
          setLoading(false);
          return;
        } else {
          // Auth expired, clear stored data
          localStorage.removeItem('whistlerUser');
          localStorage.removeItem('whistlerAuth');
        }
      } catch (error) {
        console.error('Error loading saved user:', error);
        localStorage.removeItem('whistlerUser');
        localStorage.removeItem('whistlerAuth');
      }
    }
    
    setLoading(false);
  }, []);

  function signInWithPhone(phoneNumber) {
    return new Promise((resolve, reject) => {
      try {
        // Ensure recaptcha container exists
        let container = document.getElementById('recaptcha-container');
        if (!container) {
          container = document.createElement('div');
          container.id = 'recaptcha-container';
          document.body.appendChild(container);
        }

        // Clear any existing verifier
        if (window.recaptchaVerifier) {
          window.recaptchaVerifier.clear();
        }

        // Create invisible reCAPTCHA verifier
        const recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'invisible',
          callback: (response) => {
            // reCAPTCHA solved automatically
          },
          'expired-callback': () => {
            reject(new Error('reCAPTCHA expired'));
          }
        });

        // Store verifier for cleanup
        window.recaptchaVerifier = recaptchaVerifier;

        signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier)
          .then((confirmationResult) => {
            setConfirmationResult(confirmationResult);
            resolve(confirmationResult);
          })
          .catch((error) => {
            console.error('Phone authentication error:', error);
            // Clean up verifier on error
            if (recaptchaVerifier) {
              try {
                recaptchaVerifier.clear();
              } catch (e) {
                console.warn('Error clearing recaptcha verifier:', e);
              }
            }
            reject(error);
          });
      } catch (error) {
        console.error('Error setting up reCAPTCHA:', error);
        reject(error);
      }
    });
  }

  function verifyCode(code) {
    if (!confirmationResult) {
      return Promise.reject(new Error('No confirmation result available. Please request a new verification code.'));
    }
    
    return confirmationResult.confirm(code).then((result) => {
      // Save user data to localStorage for persistent login
      const userData = {
        uid: result.user.uid,
        phoneNumber: result.user.phoneNumber,
        displayName: result.user.displayName,
        email: result.user.email
      };
      
      const authData = {
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days
        loginTime: new Date().toISOString()
      };
      
      localStorage.setItem('whistlerUser', JSON.stringify(userData));
      localStorage.setItem('whistlerAuth', JSON.stringify(authData));
      
      return result;
    }).catch((error) => {
      console.error('Verification error:', error);
      // Clear the confirmation result on error so user can request a new code
      setConfirmationResult(null);
      throw error;
    });
  }

  function signOut() {
    // Clear localStorage data
    localStorage.removeItem('whistlerUser');
    localStorage.removeItem('whistlerAuth');
    localStorage.removeItem('userName');
    localStorage.removeItem('devMode');
    
    return firebaseSignOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signInWithPhone,
    verifyCode,
    signOut,
    confirmationResult
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
      <div id="recaptcha-container"></div>
    </AuthContext.Provider>
  );
}
